import React from 'react';
import executiveTeamImg from '../assets/pexels-rdne-5922204.jpg';

const ExecutiveTalent = () => {
  const approachSteps = [
    {
      step: "01",
      title: "Comprehensive Introduction",
      description: "An extensive introduction to your organisation is the first step so that we can understand your needs, difficulties, culture, and other aspects that will influence our procedure."
    },
    {
      step: "02",
      title: "Strategic Assessment",
      description: "We analyze your leadership requirements and organizational structure to identify the ideal candidate profile that aligns with your business objectives."
    },
    {
      step: "03",
      title: "Talent Identification",
      description: "Leveraging our extensive network and cutting-edge technology to identify and vet top-tier executive talent that matches your specific needs."
    },
    {
      step: "04",
      title: "Successful Placement",
      description: "We ensure smooth onboarding and provide ongoing support to guarantee long-term success and integration within your organization."
    }
  ];

  const leadershipPrinciples = [
    {
      icon: "🎯",
      title: "Performance Focus",
      description: "In order to achieve success and lay the groundwork for a devoted, contented, and committed workforce, we prioritise performance."
    },
    {
      icon: "🌍",
      title: "Global Expertise",
      description: "We use our extensive industry knowledge to help our teams deliver the outcomes you require in local contexts across North America, Europe, and UK markets."
    },
    {
      icon: "🤝",
      title: "Relationship Building",
      description: "We provide our consultants the knowledge they need to comprehend the market and establish enduring relationships with its highly successful members."
    },
    {
      icon: "💡",
      title: "Technology Innovation",
      description: "The rest is handled by our investment in technology, allowing our consultants to work effectively. This enables us to deliver swiftness and dependability."
    }
  ];

  return (
    <div className="pt-24">
      {/* Enhanced Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Executive Talent Solutions
              </h1>
              <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
                The Strategic Leadership Your Organization Needs to Succeed
              </p>
              <p className="text-lg text-blue-200 mb-8">
                We at Adroitone, Inc. do more than just offer top-notch executive search services. 
                We handle your leadership issue in a special way.
              </p>
              <button className="bg-white text-blue-600 hover:bg-blue-50 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl shadow-lg text-lg">
                Find Your Leader
              </button>
            </div>
            
            {/* Hero Image */}
            <div className="relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-2 h-96 flex items-center justify-center border border-white/20 overflow-hidden">
                <img 
                  src={executiveTeamImg} 
                  alt="Executive leadership team collaborating" 
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-cyan-400 rounded-full opacity-20 blur-xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-300 rounded-full opacity-20 blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Executive Search Approach
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Defining New Staffing Techniques with Services of the highest degree and Outstanding leadership solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {approachSteps.map((step, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-2xl mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Principles Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Outstanding Leadership Solutions, Extraordinary Services
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Meeting and exceeding the expectations of our clients and applicants is our top focus.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {leadershipPrinciples.map((principle, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="flex items-start">
                  <div className="text-4xl mr-6">{principle.icon}</div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{principle.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{principle.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Adroitone Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                About Adroitone
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Adroitone is a global company that specialises in providing talent solution services to some of the most well-known businesses worldwide, across all sectors. In the fields of information technology, digital, executive search, and engineering services, Adroitone has now established itself as a top provider of recruiting services.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We are now concentrating on the North American, European, and UK markets. Our personnel have a thorough awareness of the trends in the local markets, which has allowed us to be successful in each of these regions. This makes it possible for us to quickly and reliably provide our services.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We have a solid grasp of our clients' cultures and business objectives thanks to our close interactions and strong collaborations with them. This aids in the identification and pre-screening of applicants who suit the organization's culture as well as the position in question.
              </p>
            </div>
            
            {/* About Image Placeholder */}
            <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl p-8 h-96 flex items-center justify-center text-white">
              <div className="text-center">
                <div className="text-6xl mb-4">🏢</div>
                <h3 className="text-2xl font-bold mb-2">Global Reach</h3>
                <p className="text-blue-100">Serving clients worldwide since 2010</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-cyan-500 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Leadership Team?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Connect with top-tier executive talent that aligns with your vision and drives exceptional results.
          </p>
          <button className="bg-white text-blue-600 hover:bg-blue-50 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl shadow-lg text-lg">
            Start Your Executive Search
          </button>
        </div>
      </section>
    </div>
  );
};

export default ExecutiveTalent;